import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import { format, parse, isValid } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';
import api from '../services/api';
import AdminSidebar from '../components/AdminSidebar';
import DashboardHeader from '../components/DashboardHeader';
import { parseToIso } from '../utils/dateDdMmYyyy';
import './Dashboard.css';

const DATE_DISPLAY = 'dd/MM/yyyy';

function displayStringToDate(str) {
  const t = String(str ?? '').trim();
  if (!t) return null;
  const d = parse(t, DATE_DISPLAY, new Date());
  return isValid(d) ? d : null;
}

function CreateAccount() {
  const [count, setCount] = useState('1');
  const [plan, setPlan] = useState('free');
  const [validTill, setValidTill] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const handleCreate = async () => {
    const n = parseInt(String(count).trim(), 10);
    if (Number.isNaN(n) || n < 1) {
      setError('Number of new accounts must be a positive integer.');
      setResult(null);
      return;
    }
    if (plan === 'premium') {
      const endIso = parseToIso(validTill);
      if (!endIso) {
        setError('Valid till must be a valid date (dd/mm/yyyy or yyyy-mm-dd).');
        setResult(null);
        return;
      }
    }
    setSubmitLoading(true);
    setError('');
    setResult(null);
    try {
      const payload = { count: n, plan };
      if (plan === 'premium') {
        payload.periodEndDate = validTill.trim();
      }
      const res = await api.post('/data/create-test-student-accounts', payload);
      setResult({
        created: res.data.created,
        accounts: res.data.accounts || [],
        periodEndDate: res.data.periodEndDate,
      });
    } catch (err) {
      const d = err.response?.data?.details;
      const e = err.response?.data?.error;
      setError([d, e].filter(Boolean).join(' — ') || 'Failed to create accounts');
    } finally {
      setSubmitLoading(false);
    }
  };

  const summaryLine =
    result != null
      ? `${result.created} account${result.created === 1 ? ' was' : 's were'} created.${
          result.periodEndDate
            ? ` Premium valid till ${result.periodEndDate} (subscription end).`
            : ''
        }`
      : '';

  const credentialsBlock =
    result != null && result.accounts.length > 0
      ? result.accounts
          .map(
            (a) =>
              `username: ${a.username}\npassword: ${a.password}\nemail (login): ${a.email}`
          )
          .join('\n\n')
      : '';

  return (
    <div className="dashboard">
      <DashboardHeader title="Create Account" />

      <div className="dashboard-layout">
        <AdminSidebar />
        <div className="dashboard-content" style={{ maxWidth: 720, margin: '0 auto' }}>
          {error ? <div className="message error">{error}</div> : null}

          <div className="form-section">
            <h2>Create Account</h2>
            <p style={{ color: '#555', marginTop: 8 }}>
              Bulk test students: usernames <code>testuser1</code>, <code>testuser2</code>, … — login
              auth email, plan free (1) or premium (2). Password equals username (bcrypt stored).
              Premium requires valid till (subscription end, same as Subscription page).
            </p>
          </div>

          <div className="form-section insert-form" style={{ marginBottom: 24 }}>
            <div className="form-group">
              <label>
                Number of new accounts <span className="required">*</span>
              </label>
              <input
                type="number"
                min={1}
                max={100}
                value={count}
                onChange={(e) => setCount(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Plan</label>
              <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                <label style={{ fontWeight: 'normal', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="plan"
                    value="free"
                    checked={plan === 'free'}
                    onChange={() => setPlan('free')}
                  />{' '}
                  Free
                </label>
                <label style={{ fontWeight: 'normal', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="plan"
                    value="premium"
                    checked={plan === 'premium'}
                    onChange={() => setPlan('premium')}
                  />{' '}
                  Premium
                </label>
              </div>
            </div>
            {plan === 'premium' ? (
              <div className="form-group">
                <label>
                  Valid till <span className="required">*</span>
                </label>
                <DatePicker
                  selected={displayStringToDate(validTill)}
                  onChange={(date) => setValidTill(date ? format(date, DATE_DISPLAY) : '')}
                  dateFormat={DATE_DISPLAY}
                  placeholderText="dd/mm/yyyy"
                  autoComplete="off"
                  isClearable
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={100}
                  dropdownMode="select"
                />
              </div>
            ) : null}
            <button
              type="button"
              className="submit-button"
              disabled={
                submitLoading ||
                String(count).trim() === '' ||
                (plan === 'premium' && String(validTill).trim() === '')
              }
              onClick={handleCreate}
            >
              {submitLoading ? 'Creating…' : 'Create'}
            </button>
          </div>

          {result != null ? (
            <div className="form-section insert-form">
              <div className="message success" style={{ marginBottom: 12 }}>
                {summaryLine}
              </div>
              <label>Username &amp; password (and email for login)</label>
              <pre
                style={{
                  whiteSpace: 'pre-wrap',
                  maxHeight: 360,
                  overflow: 'auto',
                  padding: 12,
                  background: '#f6f6f6',
                  border: '1px solid #ddd',
                  fontSize: 13,
                  margin: '8px 0 0',
                }}
              >
                {credentialsBlock}
              </pre>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default CreateAccount;
